// src/contexts/AuthContext.jsx
// REPLACES your existing AuthContext.jsx
// Changes: tracks onboardingCompleted, exposes it, updates it after onboarding

import { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../services/api';
import toast from 'react-hot-toast';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [onboardingCompleted, setOnboardingCompleted] = useState(true); // default true to avoid flash

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('adminToken');

    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await authAPI.getProfile();
      if (response.success) {
        setAdmin(response.data);
        setIsAuthenticated(true);
        // Profile response includes broker info — check onboarding flag
        // The /auth/me endpoint returns admin data; we need broker settings
        await checkOnboardingStatus();
      }
    } catch (error) {
      localStorage.removeItem('adminToken');
      localStorage.removeItem('refreshToken');
    } finally {
      setLoading(false);
    }
  };

  const checkOnboardingStatus = async () => {
    try {
      // Reuse existing settingsAPI.getBroker() which calls GET /broker/settings
      const response = await fetch('/api/broker/settings', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('adminToken')}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        setOnboardingCompleted(!!data.data.onboarding_completed);
      }
    } catch {
      // If we can't check, assume completed to avoid blocking the user unexpectedly
      setOnboardingCompleted(true);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await authAPI.login(email, password);

      if (response.success) {
        const { token, refreshToken, admin } = response.data;

        localStorage.setItem('adminToken', token);
        localStorage.setItem('refreshToken', refreshToken);

        setAdmin(admin);
        setIsAuthenticated(true);

        // Check onboarding right after login
        await checkOnboardingStatus();

        toast.success('Logged in successfully');
        return { success: true };
      }
    } catch (error) {
      const message = error.response?.data?.error || 'Login failed';
      return { success: false, error: message };
    }
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch {
      // ignore
    } finally {
      setAdmin(null);
      setIsAuthenticated(false);
      setOnboardingCompleted(true);
      localStorage.removeItem('adminToken');
      localStorage.removeItem('refreshToken');
      toast.success('Logged out successfully');
    }
  };

  // Called by OnboardingModal after successful submission
  const completeOnboarding = () => {
    setOnboardingCompleted(true);
  };

  const value = {
    admin,
    loading,
    isAuthenticated,
    onboardingCompleted,
    login,
    logout,
    completeOnboarding,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}